import { useState, useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const WARNING_BEFORE_MS = 1 * 60 * 1000; // Show warning 1 minute before timeout
const THROTTLE_MS = 30 * 1000; // Throttle activity events to every 30 seconds

/**
 * Hook to track user idle time and trigger auto-logout.
 *
 * @param {function} onLogout - called when idle timeout expires
 * @param {object} options
 * @param {number} options.timeout - idle timeout in ms (default 10 min)
 * @param {number} options.warningBefore - show warning this many ms before timeout (default 1 min)
 * @returns {{ showWarning, countdown, stayLoggedIn, dismissWarning }}
 */
export function useIdleTimer(onLogout, options = {}) {
    const timeout = options.timeout || IDLE_TIMEOUT_MS;
    const warningBefore = options.warningBefore || WARNING_BEFORE_MS;
    const warningAt = timeout - warningBefore;

    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(Math.floor(warningBefore / 1000));

    const lastActivityRef = useRef(Date.now());
    const warningShownRef = useRef(false);
    const timerRef = useRef(null);
    const countdownRef = useRef(null);
    const throttleRef = useRef(0);

    const resetTimer = useCallback(() => {
        lastActivityRef.current = Date.now();
        warningShownRef.current = false;
        setShowWarning(false);
        setCountdown(Math.floor(warningBefore / 1000));

        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    }, [warningBefore]);

    // Handle user activity — throttled, and ignored while warning is showing
    const handleActivity = useCallback(() => {
        if (warningShownRef.current) return; // Don't reset during warning

        const now = Date.now();
        if (now - throttleRef.current < THROTTLE_MS) return;
        throttleRef.current = now;

        lastActivityRef.current = now;
    }, []);

    // Main polling interval
    useEffect(() => {
        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - lastActivityRef.current;

            if (elapsed >= timeout) {
                // Time's up — logout
                clearInterval(timerRef.current);
                if (countdownRef.current) clearInterval(countdownRef.current);
                onLogout();
                return;
            }

            if (elapsed >= warningAt && !warningShownRef.current) {
                // Show warning
                warningShownRef.current = true;
                setShowWarning(true);

                const remaining = Math.floor((timeout - elapsed) / 1000);
                setCountdown(remaining);

                // Start countdown
                countdownRef.current = setInterval(() => {
                    const nowElapsed = Date.now() - lastActivityRef.current;
                    const secs = Math.max(0, Math.floor((timeout - nowElapsed) / 1000));
                    setCountdown(secs);

                    if (secs <= 0) {
                        clearInterval(countdownRef.current);
                        countdownRef.current = null;
                        onLogout();
                    }
                }, 1000);
            }
        }, 5000); // Check every 5 seconds

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [timeout, warningAt, onLogout]);

    // Attach activity listeners
    useEffect(() => {
        const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));
        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
        };
    }, [handleActivity]);

    const stayLoggedIn = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    return { showWarning, countdown, stayLoggedIn };
}
